import { IEquipment } from '@/pages/Equipment/equipment.types'
import { IParameter } from '@/pages/Parameters/parameters.types'
import { useRef, useEffect, useMemo } from 'react'
import { TLastValuesDataRef } from '../data.types'
import useDataRef, { IUseDataRefProps } from './useDataRef'

export interface IUseDataUpToTsRef extends Omit<IUseDataRefProps, 'live'> {
  tsCurrent: number
  onNewValueReceived?: () => void
}

const useDataPointUpToTsRef = (props: IUseDataUpToTsRef) => {
  const dataPointRef = useRef<TLastValuesDataRef>(initLastPointZeros(props.devices, props.parameters))

  const config: IUseDataRefProps = useMemo(
    () => ({
      initialTsFrom: props.initialTsFrom,
      initialTsTo: props.initialTsTo,
      devices: props.devices,
      parameters: props.parameters,
      aggregate: props.aggregate ?? false,
      live: false,
    }),
    [props]
  )

  const { dataRef, loading } = useDataRef(config)

  useEffect(() => {
    if (loading || !dataRef.current) return

    const dataByTs: Record<IEquipment['id'], Record<number, Record<IParameter['id'], number>>> = {}
    for (const equipmentId in dataRef.current) {
      dataByTs[equipmentId] = {}
      for (const point of dataRef.current[equipmentId]) {
        dataByTs[equipmentId][point.ts] = Object.fromEntries(point.values.map((v) => [v.parameterId, v.value]))
      }
    }

    let isValueUpdated = false

    for (const equipmentId in dataByTs) {
      for (const ts in dataByTs[equipmentId]) {
        if (Number(ts) <= props.tsCurrent) {
          isValueUpdated = true
          dataPointRef.current[equipmentId] = { ...dataByTs[equipmentId][ts] }
        } else break
      }
    }

    if (props.onNewValueReceived && isValueUpdated) {
      props.onNewValueReceived()
    }
  }, [props.tsCurrent, props.onNewValueReceived])

  return {
    ref: dataPointRef,
    loading: loading,
  }
}

const initLastPointZeros = (equipmentIds: IEquipment['id'][], parameterId: IParameter['id'][]) => {
  const res: TLastValuesDataRef = {}
  for (const id of equipmentIds) {
    res[id] = Object.fromEntries(parameterId.map((p) => [p, 0])) as Record<IParameter['id'], number>
  }
  return res
}

export default useDataPointUpToTsRef
